import { __rest } from 'tslib';
import { LogLevel } from './types/LogLevel.es.js';
import { isClassLogEnable, getRealLogLevel, prepareEvent, shouldLogToProvider, isStructuredEvent } from './utils.es.js';

class LogService {
    constructor(logger, config, callback = () => { }, className) {
        this.logger = logger;
        this.config = config;
        this.callback = callback;
        this.className = className;
    }
    // region Public methods
    getInstance(className) {
        const newClassName = this.className ? `${this.className}${this.config.separator}${className}` : className;
        return new LogService(this.logger, this.config, this.callback, newClassName);
    }
    log(message, eventId, extraArguments) {
        this.prepare(LogLevel.LOG).apply(null, arguments);
    }
    debug(message, eventId, extraArguments) {
        this.prepare(LogLevel.DEBUG).apply(null, arguments);
    }
    info(message, eventId, extraArguments) {
        this.prepare(LogLevel.INFO).apply(null, arguments);
    }
    warn(message, eventId, error, extraArguments) {
        this.prepare(LogLevel.WARN).apply(null, arguments);
    }
    error(message, eventId, error, extraArguments) {
        this.prepare(LogLevel.ERROR).apply(null, arguments);
    }
    fatal(message, eventId, error, extraArguments) {
        this.prepare(LogLevel.FATAL).apply(null, arguments);
    }
    isLevelEnabled(level) {
        return this.config.enabled && isClassLogEnable(this.className, level, this.config.module);
    }
    // endregion
    // region Private methods
    getLogFunction(level) {
        switch (level) {
            case LogLevel.INFO:
                return this.logger.info;
            case LogLevel.WARN:
                return this.logger.warn;
            case LogLevel.DEBUG:
                return this.logger.debug;
            case LogLevel.ERROR:
                return this.logger.error;
            case LogLevel.FATAL:
                return this.logger.error;
            case LogLevel.LOG:
                return this.logger.log;
            default:
                return this.logger.log; // Should never happen
        }
    }
    prepare(level) {
        return (...args) => {
            if (this.className) {
                args[0] = `${this.className}${this.config.separator}${args[0]}`;
            }
            const newLevel = getRealLogLevel(args, level);
            if (!this.isLevelEnabled(newLevel)) {
                return;
            }
            const event = prepareEvent(newLevel, args, this.className);
            if (shouldLogToProvider(newLevel, this.config.console)) {
                const eventId = isStructuredEvent(event) ? event.eventId : null;
                const fn = this.getLogFunction(newLevel);
                const _a = event, extraParams = __rest(_a, ["level", "message", "eventId"]);
                const stack = extraParams && extraParams.error && extraParams.error.stack;
                if (this.config.debug && stack) {
                    // For debugging purpose, this will print original stack trace with source mapping
                    console.error(stack);
                }
                fn.apply(null, [event.message, eventId, extraParams]);
            }
            // Callback if somebody want to deal with sent message
            this.callback(event);
        };
    }
}

export { LogService };
